import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { ProductsType } from 'src/constants/products';
import { PATH_ONBOARDING } from 'src/constants/spa-routes';
import { FormProvider, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import {
  escalationWorkflowSchema,
  EscalationFormValues,
  getEscalationFormValues,
} from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep6/escalationWorkflowForm';
import { useAppSelector } from 'src/store';
import {
  useDeployConfigurationSettingsMutation,
  useGetConfigurationSettingsPromptQuery,
  useGetDefaultConfigurationTemplateFieldsQuery,
  useUpdateConfigurationSettingsPromptMutation,
} from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import { EscalatedList } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep6/EscalatedList/EscalatedList';
import { getWorkflowRequestAction } from 'src/pages/ConfigurationPage/WorkflowsTab/getWorkflowRequestAction';
import { validateWorkflowActions } from 'src/helpers/validateWorkflowActions';
import { usePagedConfigurationSettings } from 'src/pages/ConfigurationPage/hooks/usePagedConfigurationSettings';

const OnboardingStep6 = () => {
  const [activeSettings, setActiveSettings] = useState(null);
  const [inputData, setInputData] = useState(null);
  const loader = useLoader();
  const [workflow, setWorkflow] = useState(inputData?.pf_default_workflow || null);
  const { project } = useAppSelector((s) => s.onboardingSlice);
  const { data: defaultTemplateFields, isLoading: isLoadingDefaultTemplateFields } =
    useGetDefaultConfigurationTemplateFieldsQuery();
  const { settings, isLoadingAllConfigurationSettings } = usePagedConfigurationSettings(
    project.id,
    { skip: !(project.id && defaultTemplateFields) },
    [],
  );
  const [
    updateConfigurationSettings,
    { data: updateConfigurationSettingsData, isLoading: isLoadingUpdateConfigurationSettings },
  ] = useUpdateConfigurationSettingsPromptMutation();
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: project.id, settings_id: activeSettings?.id },
      { skip: !(project.id && activeSettings && defaultTemplateFields) },
    );
  const [
    deployConfigurationSettings,
    {
      isLoading: isLoadingDeployConfigurationSettings,
      isSuccess: isSuccessDeployConfigurationSettings,
    },
  ] = useDeployConfigurationSettingsMutation();

  const navigate = useNavigate();

  useEffect(() => {
    if (defaultTemplateFields) {
      if (!activeSettings && settings.length) {
        setActiveSettings(settings[0]);
      }

      if (activeSettings) {
        const newActiveSettings = settings.find((item) => {
          return item.id === activeSettings.id;
        });

        setActiveSettings(newActiveSettings);
      } else {
        setInputData(null);
      }
    }
  }, [settings, defaultTemplateFields]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      setInputData(configurationSettingsPrompt.input_data);
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    if (isSuccessDeployConfigurationSettings) {
      next();
    }
  }, [isSuccessDeployConfigurationSettings]);

  useEffect(() => {
    if (
      isLoadingDefaultTemplateFields ||
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingUpdateConfigurationSettings ||
      isLoadingDeployConfigurationSettings
    ) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [
    isLoadingDefaultTemplateFields,
    isLoadingAllConfigurationSettings,
    isLoadingConfigurationSettingsPrompt,
    isLoadingUpdateConfigurationSettings,
    isLoadingDeployConfigurationSettings,
  ]);

  useEffect(() => {
    if (inputData) {
      const workflow = inputData?.pf_default_workflow;

      methods.reset(getEscalationFormValues(workflow));

      setWorkflow(workflow);
    }
  }, [inputData]);

  useEffect(() => {
    if (updateConfigurationSettingsData) {
      next();
    }
  }, [updateConfigurationSettingsData]);

  const back = () => {
    navigate(`${PATH_ONBOARDING.ROOT}/5?type=${ProductsType.customer}`, { replace: true });
  };

  const next = () => {
    navigate(`${PATH_ONBOARDING.ROOT}/7?type=${ProductsType.customer}`, { replace: true });
  };

  const methods = useForm<EscalationFormValues>({
    defaultValues: async () => getEscalationFormValues(workflow),
    resolver: formResolver(escalationWorkflowSchema),
  });

  const submit = (data: EscalationFormValues) => {
    const is = {
      valid: validateWorkflowActions(data, methods.setError),
      dirty: Boolean(Object.keys(methods.formState.dirtyFields).length),
      deployed: activeSettings?.is_deployed,
    };

    if (!is.valid) {
      return;
    }

    if (!is.dirty) {
      return is.deployed
        ? next()
        : deployConfigurationSettings({ project_id: project.id, settings_id: activeSettings.id });
    }

    if (activeSettings) {
      const action = data.action.map((action) => getWorkflowRequestAction(action));
      const newWorkflow = {
        enabled: workflow.enabled,
        type: workflow.type,
        action: action[0],
      };

      const body = {
        input_data: {
          ...inputData,
          pf_default_workflow: newWorkflow,
        },
      };

      updateConfigurationSettings({
        project_id: project.id,
        settings_id: activeSettings.id,
        body,
      }).then((updateConfigurationSettingsResult) => {
        if (!('error' in updateConfigurationSettingsResult)) {
          deployConfigurationSettings({ project_id: project.id, settings_id: activeSettings.id });
        }
      });
    }
  };

  return (
    <div className={'w-full max-w-[650px] h-[65%] max-h-[65%]'}>
      <div className={'flex flex-col gap-[5px] px-[50px] py-1 h-full items-center overflow-y-auto'}>
        <FormProvider {...methods}>
          <form
            id={'onboarding-step-5'}
            className={'flex flex-col gap-5 w-full min-w-[455px] max-w-[455px]'}
            onSubmit={methods.handleSubmit(submit)}
          >
            <EscalatedList
              control={methods.control}
              watch={methods.watch}
              resetField={methods.resetField}
            />
          </form>
        </FormProvider>
      </div>
      <div className={'mt-5 flex items-center justify-center gap-5 px-[50px]'}>
        <div
          className={
            'flex flex-col items-center justify-center gap-5 w-full min-w-[455px] max-w-[455px]'
          }
        >
          <Button form={'onboarding-step-5'} className="w-full py-6 font-semibold">
            <p>Continue</p>
          </Button>
          <button
            type="button"
            onClick={back}
            className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep6;
